import { motion } from "framer-motion";
import { ArrowRight, ShieldCheck, HeartHandshake, Clock, LifeBuoy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { CrisisFooter } from "@/components/sim/CrisisFooter";

const HERO_IMG = "/hero-counseling.jpg";

const PILLARS = [
  { icon: ShieldCheck, label: "Safety" },
  { icon: HeartHandshake, label: "Trust" },
  { icon: Clock, label: "Time" },
  { icon: LifeBuoy, label: "Resources" },
];

export const Hero = ({ onStart }) => {
  return (
    <section data-testid="hero" className="relative overflow-hidden">
      <div className="absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-emerald-50/60 to-transparent pointer-events-none" />

      <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-16 pb-16 sm:pt-20 sm:pb-24">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
          {/* Left: copy */}
          <div>
            <motion.p
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              className="inline-block text-[11px] font-semibold uppercase tracking-[0.18em] text-emerald-800 bg-emerald-100/70 rounded-full px-3 py-1"
            >
              Domestic violence victim assistance training
            </motion.p>

            <motion.h1
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.08 }}
              className="mt-5 font-heading text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight text-stone-900"
            >
              Safe Path Simulator
            </motion.h1>

            <motion.p
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.16 }}
              className="mt-5 text-lg sm:text-xl leading-relaxed text-stone-600 max-w-xl"
            >
              Practice survivor-centered decisions under pressure. Work realistic cases, from an
              in-person intake to a late-night crisis call to a teen calling for help, and balance{" "}
              <strong className="text-stone-800 font-medium">safety</strong>,{" "}
              <strong className="text-stone-800 font-medium">trust</strong>,{" "}
              <strong className="text-stone-800 font-medium">time</strong>, and{" "}
              <strong className="text-stone-800 font-medium">resources</strong> one choice at a time.
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 18 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.55, delay: 0.24 }}
              className="mt-8 flex flex-wrap items-center gap-4"
            >
              <Button
                data-testid="start-button"
                onClick={onStart}
                className="rounded-full bg-emerald-800 hover:bg-emerald-900 text-white h-12 px-7 text-base shadow-sm"
              >
                Choose a case
                <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
              <span className="text-sm text-stone-500">10 cases · shuffled choices · AI reflection</span>
            </motion.div>

            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ duration: 0.6, delay: 0.35 }}
              className="mt-10 grid grid-cols-2 sm:grid-cols-4 gap-3 max-w-lg"
            >
              {PILLARS.map((p) => (
                <div key={p.label} className="flex items-center gap-2.5 rounded-xl border border-stone-200 bg-white/80 backdrop-blur px-4 py-3">
                  <p.icon className="h-4 w-4 text-emerald-700" />
                  <span className="text-sm font-medium text-stone-700">{p.label}</span>
                </div>
              ))}
            </motion.div>
          </div>

          {/* Right: framed photo */}
          <motion.div
            initial={{ opacity: 0, scale: 0.97 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.7, delay: 0.2, ease: "easeOut" }}
            className="relative"
          >
            <div className="relative rounded-[2rem] overflow-hidden shadow-xl ring-1 ring-stone-200/70 aspect-[4/3] sm:aspect-[5/4]">
              <img
                src={HERO_IMG}
                alt="An advocate listening to a survivor in a calm, warmly lit office"
                className="absolute inset-0 w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-stone-900/25 via-transparent to-transparent" />
            </div>

            <div className="absolute -bottom-5 left-5 right-5 sm:left-8 sm:right-auto sm:max-w-xs rounded-2xl bg-white/95 backdrop-blur border border-stone-200 shadow-lg p-4">
              <div className="flex items-center gap-2 text-emerald-800">
                <HeartHandshake className="h-4 w-4" />
                <span className="text-[11px] font-semibold uppercase tracking-wider">Trauma-informed practice</span>
              </div>
              <p className="mt-1.5 text-sm leading-snug text-stone-600">
                Every choice shapes a survivor's safety, trust, and next steps.
              </p>
            </div>
          </motion.div>
        </div>

        <p data-testid="wellbeing-note" className="mt-16 sm:mt-14 max-w-2xl text-sm leading-relaxed text-stone-600 border-l-2 border-emerald-400 pl-4">
          Many advocates bring lived experience to this work. Some cases include realistic sounds,
          like children in the background or a door slam. You can adjust or mute the audio at any
          time using the control in the corner; please take care of yourself as you train.
        </p>
        <CrisisFooter className="mt-4" />
        <p className="mt-3 max-w-2xl text-[11px] leading-relaxed text-stone-400 pl-4">
          Ambient sound credits: post office ambience by radio aporee (CC BY-SA 3.0), night marsh
          recording (public domain, archive.org), newborn cry (CC0, Wikimedia Commons), door slam by
          Gravity Sound (CC BY, Wikimedia Commons).
        </p>
      </div>
    </section>
  );
};
